import { type Ctx, obox, pline, ptext, R } from "./pixel";

export interface MonitorData {
  hr: number;
  spo2: number;
  rr: number;
  sbp?: number | null;
  dbp?: number | null;
  /** Clock time of the last NIBP cycle. */
  nibpT?: string | null;
  /** End-tidal CO2; only shown when intubated / on capnography. */
  co2?: number | null;
  alarm?: boolean;
}

export interface CtgData {
  fhr: number;
  /** Minimal variability (a non-reassuring trace). */
  lowVar?: boolean;
  /** Seconds between contractions on the toco channel. */
  ctxEvery: number;
  ctxAmp: number;
  alarm?: boolean;
}

/** Scrolling lead-II trace. */
export function drawEcg(
  c: Ctx,
  x: number,
  y: number,
  w: number,
  amp: number,
  hr: number,
  now: number,
  col: string,
): void {
  c.fillStyle = col;
  let py = y;
  for (let i = 0; i < w; i++) {
    const ts = now / 1000 - (w - i) / 40;
    const p = ((((ts * hr) / 60) % 1) + 1) % 1;
    let dy = 0;
    if (p > 0.1 && p < 0.12) dy = -amp;
    else if (p >= 0.12 && p < 0.14) dy = amp * 0.35;
    else if (p > 0.3 && p < 0.42) dy = -amp * 0.25 * Math.sin(((p - 0.3) / 0.12) * Math.PI);
    const yy = Math.round(y + dy);
    if (i === 0) py = yy;
    c.fillRect(x + i, Math.min(py, yy), 1, Math.abs(yy - py) + 1);
    py = yy;
  }
}

/** Scrolling SpO2 plethysmograph. */
export function drawPleth(
  c: Ctx,
  x: number,
  y: number,
  w: number,
  amp: number,
  hr: number,
  now: number,
  col: string,
): void {
  c.fillStyle = col;
  let py = y;
  for (let i = 0; i < w; i++) {
    const ts = now / 1000 - (w - i) / 40;
    const p = ((((ts * hr) / 60 + 0.2) % 1) + 1) % 1;
    const v =
      p < 0.35
        ? Math.sin(((p / 0.35) * Math.PI) / 2)
        : Math.max(0, 1 - (p - 0.35) / 0.65) * (0.85 + 0.15 * Math.sin((p - 0.35) * 12));
    const yy = Math.round(y - v * amp);
    if (i === 0) py = yy;
    c.fillRect(x + i, Math.min(py, yy), 1, Math.abs(yy - py) + 1);
    py = yy;
  }
}

/** Full-screen bedside / anaesthesia monitor for the 192×108 close-up canvas. */
export function drawMonitor(c: Ctx, now: number, d: MonitorData): void {
  obox(c, "#3a3f52", 3, 3, 186, 102);
  R(c, "#2a2e3e", 3, 100, 186, 5);
  R(c, "#05090a", 6, 6, 180, 92);
  for (let x = 8; x < 124; x += 8) for (let y = 8; y < 96; y += 8) R(c, "#0b1612", x, y, 1, 1);
  drawEcg(c, 8, 30, 116, 16, d.hr, now, "#3be37a");
  drawPleth(c, 8, 64, 116, 12, d.hr, now, "#4fd6ff");
  if (d.co2 != null) {
    c.fillStyle = "#ffd447";
    for (let i = 0; i < 116; i++) {
      const p = ((((now / 1000 - (116 - i) / 40) / 4) % 1) + 1) % 1;
      c.fillRect(8 + i, p > 0.15 && p < 0.6 ? 86 : 94, 1, 1);
    }
  }
  ptext(c, "II", 9, 9, "#3be37a");
  ptext(c, "PLETH", 9, 44, "#4fd6ff");
  if (d.co2 != null) ptext(c, "CO2", 9, 78, "#ffd447");
  R(c, "#1b2a24", 126, 6, 1, 92);
  ptext(c, "HR", 130, 9, "#3be37a");
  ptext(c, d.hr, 136, 16, "#3be37a", 3);
  ptext(c, "SPO2", 130, 36, "#4fd6ff");
  ptext(c, `${d.spo2}%`, 136, 43, "#4fd6ff", 2);
  ptext(c, `NIBP ${d.nibpT ?? "--:--"}`, 130, 58, "#ff6a6a");
  ptext(c, d.sbp != null ? `${d.sbp}/${d.dbp ?? "--"}` : "---/---", 130, 65, "#ff6a6a", 2);
  ptext(c, (d.co2 != null ? `ETCO2 ${d.co2}  ` : "") + `RR ${d.rr}`, 130, 86, "#ffd447");
  if (d.alarm && Math.floor(now / 400) % 2) obox(c, "#ff3b4e", 172, 8, 10, 6);
}

/** Cardiotocograph strip (FHR + toco) for the close-up canvas. */
export function drawCTG(c: Ctx, now: number, d: CtgData): void {
  obox(c, "#3a3f52", 3, 3, 186, 102);
  R(c, "#2a2e3e", 3, 100, 186, 5);
  R(c, "#f6eef0", 6, 6, 150, 96);
  for (let x = 6; x < 156; x += 6) R(c, (x - 6) % 30 === 0 ? "#e2b8c4" : "#efd6dc", x, 6, 1, 96);
  for (let y = 8; y < 62; y += 6) R(c, "#efd6dc", 6, y, 150, 1);
  for (let y = 70; y < 102; y += 8) R(c, "#efd6dc", 6, y, 150, 1);
  R(c, "#d8a0b0", 6, 64, 150, 2);
  const yF = (f: number) => 60 - ((f - 60) / 140) * 52;
  for (const ref of [110, 160]) {
    const y = Math.round(yF(ref));
    for (let x = 6; x < 156; x += 3) R(c, "#c08aa0", x, y, 1, 1);
  }
  const sec = now / 1000;
  const W = 150;
  let py: number | null = null;
  let pt: number | null = null;
  for (let i = 0; i < W; i++) {
    const tm = sec * 3 - (W - i);
    const varAmp = d.lowVar ? 1.5 : 6;
    let f =
      d.fhr +
      varAmp * Math.sin(tm * 0.9) +
      varAmp * 0.6 * Math.sin(tm * 2.3 + 1) +
      (d.lowVar ? 0 : 3 * Math.sin(tm * 5.1));
    const ph = (((tm / 180) % 1) + 1) % 1;
    if (!d.lowVar && ph > 0.3 && ph < 0.38) f += 18 * Math.sin(((ph - 0.3) / 0.08) * Math.PI);
    const y = Math.round(yF(f));
    if (py !== null) pline(c, 6 + i - 1, py, 6 + i, y, "#203a8a", 1);
    py = y;
    const cp = (((tm / d.ctxEvery) % 1) + 1) % 1;
    const toco = cp < 0.25 ? Math.sin((cp / 0.25) * Math.PI) * d.ctxAmp : 0;
    const ty = Math.round(100 - 6 - toco);
    if (pt !== null) pline(c, 6 + i - 1, pt, 6 + i, ty, "#203a8a", 1);
    pt = ty;
  }
  ptext(c, "FHR", 160, 9, "#3be37a");
  ptext(c, Math.round(d.fhr + 3 * Math.sin(sec)), 159, 17, "#3be37a", 2);
  ptext(c, "TOCO", 160, 64, "#ffd447");
  ptext(c, Math.round(10 + d.ctxAmp * 0.3), 160, 71, "#ffd447", 2);
  if (d.alarm && Math.floor(now / 400) % 2) obox(c, "#ff3b4e", 172, 90, 10, 6);
}
